import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Divider, Heading, Modal, ProgressBar, useToast } from '@zillow/constellation-10';
import {
  ActionMetadataEntryPoint,
  type GetTourConfigAndAvailabilityWebApiResponseDto,
  type LocalTime,
  type TourConfirmationType,
  TourDuration,
} from '@zg-rentals/ts-z-calendar-client';
import { DatarouterError } from '@zg-rentals/web-base';
import TourCalendarConfig from './components/TourCalendarConfig';
import TourCalendarTimeZone from './components/TourCalendarTimeZone';
import TourAvailabilityCalendarFooter from './components/TourAvailabilityCalendarFooter';
import CalendarSettings from '../TourCalendarWithSettings/components/CalendarSettings';
import UnmaskedToast from '../UnmaskedToast';
import { TourAvailabilityWizardSkeleton } from './components/TourAvailabilityWizardSkeleton';
import TourCalendar from '../TourCalendar';
import { type TimeZoneIds, TOUR_CALENDAR_MODES, TourWizardSteps } from '../../constants/TourCalendar';
import { MOBILE_HANDOFF_TYPES, mobileHandoffHandler } from '../../utils/mobileAppCallbacks';
import useTourCalendarApi from '../../utils/tourCalendarHooks';
import type { ClickStreamMetadata } from '../../constants/TouringClickStreamEvents';
import {
  CALENDAR_CLICKSTREAM_ACTION,
  emitCalendarEventActionEvent,
  emitTourAvailabilityWizardImpresssionEvent,
  emitTourAvailabilityWizardTimeZoneNextClickEvent,
  emitTourTypeSelectionEvent,
  TourAvailabilityWizardImpresssionSourceMap,
} from '../../constants/TouringClickStreamEvents';

import TourCalendarContext from '../TourCalendarWithSettings/context/TourCalendarContext';
import {
  TOUR_DURATION_SAVE_ERROR_MESSAGE,
  TOUR_DURATION_SAVE_SUCCESS_MESSAGE,
  TOUR_SETTINGS_SAVE_SUCCESS_MESSAGE,
} from '../../constants/ToastMessageConstants';
import {
  wizardBodyStyles,
  wizardContainerStyles,
  wizardContentGridStyles,
  wizardExitButtonStyles,
  wizardFooterStyles,
  wizardHeaderStyles,
  wizardHiddenHeaderStyles,
  wizardProgressStyles,
  wizardTitleStyles,
  wizardWideContentStyles,
} from './styles';

const SETTINGS_SAVE_ERROR_MESSAGE = 'Your tour setting changes couldn’t be saved. Try again.';
const TITLE_ID = 'TourAvailabilityWizard-Title';

type Props = {
  onClose: () => void;
  listingAlias: string;
  modalHeaderText: string;
  isInMobileWebView?: boolean;
  modalHeaderLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  clickStreamMetadata?: ClickStreamMetadata;
  hideCloseButton?: boolean;
  canAccessPostingPathTourDuration?: boolean;
  propertyPhotoUrl?: string;
};

const TourAvailabilityWizard = ({
  onClose,
  listingAlias,
  modalHeaderText,
  isInMobileWebView = false,
  modalHeaderLevel,
  clickStreamMetadata,
  hideCloseButton = false,
  canAccessPostingPathTourDuration = false,
  propertyPhotoUrl,
}: Props) => {
  const { enqueueToast } = useToast();
  const mode = TOUR_CALENDAR_MODES.TOUR_AVAILABILITY;
  const [wizardStep, setWizardStep] = useState<TourWizardSteps>(TourWizardSteps.CONFIG);
  const [wizardTourConfirmationType, setWizardTourConfirmationType] = useState<TourConfirmationType>();
  const [wizardTimeZone, setWizardTimeZone] = useState<TimeZoneIds>();
  const [wizardTourDuration, setWizardTourDuration] = useState<TourDuration | undefined>(TourDuration.MINUTES_30);
  const [hasEnabledTouring, setHasEnabledTouring] = useState(false);
  const [displaySettings, setDisplaySettings] = useState(false);

  const onGetTourConfigAndAvailabilitySuccess = useCallback(
    (data: GetTourConfigAndAvailabilityWebApiResponseDto) => {
      const trackTourAvailabilityWizardImpression = (source: TourAvailabilityWizardImpresssionSourceMap) => {
        if (clickStreamMetadata) {
          emitTourAvailabilityWizardImpresssionEvent(clickStreamMetadata, source);
        }
      };

      if ('tourConfig' in data && data.tourConfig) {
        const confirmationType = data.tourConfig.tourSchedulingTypes.schedulingTypes[0].confirmationType;
        const zoneId = data.tourConfig.zoneId;

        if (confirmationType && zoneId) {
          setWizardStep?.(TourWizardSteps.CALENDAR);
          setHasEnabledTouring(true);
          trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.TOUR_EVENT);
          return;
        }

        if (confirmationType) {
          setWizardStep?.(TourWizardSteps.TIME_ZONE);
          trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TIME_NEXT);
          return;
        }

        trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TYPE_NEXT);
        return;
      }

      trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TYPE_NEXT);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    [setWizardStep],
  );

  const {
    availabilityEvents,
    tourEvents,
    timeZone: timeZoneFromApi,
    tourConfirmationType: tourConfirmationTypeFromApi,
    tourDuration: tourDurationFromApi,
    addAvailabilityEvent,
    updateAvailabilityEvent,
    removeAvailabilityEvent,
    performTourAction,
    enableTouringForListing,
    disableTouringForListing,
    isGetTourConfigAndAvailabilityLoading,
    isEnableTouringForListingLoading,
    isDisableTouringForListingLoading,
    isTouringSchedulingEnabled,
    storeListingCalendarTimeConfig,
    storeListingTourDuration,
    calendarStartTime,
    calendarEndTime,
    refetchTouringData,
  } = useTourCalendarApi({
    listingAlias,
    mode,
    onGetTourConfigAndAvailabilitySuccess,
  });

  const onTourTypeSelection = (tourConfirmationType: TourConfirmationType) => {
    setWizardTourConfirmationType(tourConfirmationType);
  };

  const onTimeZoneSelection = (timeZone: TimeZoneIds) => {
    setWizardTimeZone(timeZone);
  };

  // Initialize with existing API tour duration if available
  useEffect(() => {
    if (tourDurationFromApi) {
      setWizardTourDuration(tourDurationFromApi);
    }
  }, [tourDurationFromApi]);

  const onTourDurationSelection = (selectedTourDuration: TourDuration) => {
    setWizardTourDuration(selectedTourDuration);
  };

  const handleSettingsSelectTourConfirmationType = useCallback(
    (type: TourConfirmationType) => {
      enableTouringForListing(type, timeZoneFromApi, tourDurationFromApi, ActionMetadataEntryPoint.WIZARD)
        .then(() => {
          enqueueToast(<UnmaskedToast tone="success">{TOUR_SETTINGS_SAVE_SUCCESS_MESSAGE}</UnmaskedToast>);
          return;
        })
        .catch(() => {
          enqueueToast(<UnmaskedToast tone="critical">{SETTINGS_SAVE_ERROR_MESSAGE}</UnmaskedToast>);
        });
    },
    [timeZoneFromApi, tourDurationFromApi, enableTouringForListing, enqueueToast],
  );

  const handleTourSchedulingToggle = useCallback(() => {
    disableTouringForListing()
      .then(() => onClose())
      .catch((error) => {
        const errorMessage = DatarouterError.getDisplayMessage(error);
        enqueueToast(<UnmaskedToast tone="critical">{errorMessage}</UnmaskedToast>);
      });
  }, [disableTouringForListing, enqueueToast, onClose]);

  const handleChangeCalendarConfigTime = useCallback(
    (startTime: LocalTime, endTime: LocalTime) => {
      storeListingCalendarTimeConfig(startTime, endTime)
        .then(() => {
          enqueueToast(<UnmaskedToast tone="success">{TOUR_SETTINGS_SAVE_SUCCESS_MESSAGE}</UnmaskedToast>);
          return;
        })
        .catch(() => {
          enqueueToast(<UnmaskedToast tone="critical">{SETTINGS_SAVE_ERROR_MESSAGE}</UnmaskedToast>);
        });
    },
    [enqueueToast, storeListingCalendarTimeConfig],
  );

  const handleChangeTourDuration = useCallback(
    (duration: TourDuration) => {
      storeListingTourDuration(duration)
        .then(() => {
          enqueueToast(<UnmaskedToast tone="success">{TOUR_DURATION_SAVE_SUCCESS_MESSAGE}</UnmaskedToast>);
          return;
        })
        .catch(() => {
          enqueueToast(<UnmaskedToast tone="critical">{TOUR_DURATION_SAVE_ERROR_MESSAGE}</UnmaskedToast>);
        });
    },
    [enqueueToast, storeListingTourDuration],
  );

  const isCalendarStep = wizardStep === TourWizardSteps.CALENDAR;
  const hasExitButton = !isInMobileWebView && !hideCloseButton;

  const renderBody = () => {
    if (isGetTourConfigAndAvailabilityLoading) {
      return (
        <Box css={wizardContentGridStyles}>
          <TourAvailabilityWizardSkeleton />
        </Box>
      );
    }

    let component;
    let progressBarValue = 1;

    if (wizardStep === TourWizardSteps.CONFIG) {
      component = (
        <TourCalendarConfig
          onTourTypeSelection={onTourTypeSelection}
          tourConfirmationType={wizardTourConfirmationType}
        />
      );
    } else if (wizardStep === TourWizardSteps.TIME_ZONE) {
      component = (
        <TourCalendarTimeZone
          onTimeZoneSelection={onTimeZoneSelection}
          onTourDurationSelection={onTourDurationSelection}
          timeZone={wizardTimeZone}
          tourDuration={wizardTourDuration}
          disableTimeZoneSelection={Boolean(timeZoneFromApi)}
          canAccessPostingPathTourDuration={canAccessPostingPathTourDuration}
        />
      );
      progressBarValue = 2;
    } else if (timeZoneFromApi && tourConfirmationTypeFromApi) {
      component = (
        <TourCalendarContext.Provider
          value={{
            clickStreamMetadata,
            listingAlias,
            listingAddress: modalHeaderText,
            propertyPhotoUrl,
            refetchTouringData,
          }}
        >
          <TourCalendar
            availabilityEvents={availabilityEvents}
            tourEvents={tourEvents}
            addAvailabilityEvent={addAvailabilityEvent}
            updateAvailabilityEvent={updateAvailabilityEvent}
            removeAvailabilityEvent={removeAvailabilityEvent}
            performTourAction={performTourAction}
            mode={mode}
            onClickSettings={() => setDisplaySettings(true)}
            timeZone={timeZoneFromApi}
            clickStreamMetadata={clickStreamMetadata}
            isInMobileWebView={isInMobileWebView}
            listingAlias={listingAlias}
            calendarStartTime={calendarStartTime}
            calendarEndTime={calendarEndTime}
            tourDuration={wizardTourDuration}
          />
        </TourCalendarContext.Provider>
      );
      progressBarValue = 3;
    }

    return (
      <>
        {!hasEnabledTouring && (
          <ProgressBar
            aria-label="Tour availability progress"
            maxValue={3}
            value={progressBarValue}
            data-testid="TourAvailabilityWizard-ProgressBar"
            css={wizardProgressStyles}
          />
        )}
        <Box css={isCalendarStep ? wizardWideContentStyles : wizardContentGridStyles}>{component}</Box>
      </>
    );
  };

  const onClickBack = () => {
    if (wizardStep === TourWizardSteps.TIME_ZONE) {
      setWizardStep(TourWizardSteps.CONFIG);
    } else if (wizardStep === TourWizardSteps.CALENDAR) {
      setWizardStep(TourWizardSteps.TIME_ZONE);
    }
  };

  const onClickNext = () => {
    if (wizardStep === TourWizardSteps.CONFIG) {
      if (clickStreamMetadata && wizardTourConfirmationType) {
        emitTourTypeSelectionEvent(clickStreamMetadata, wizardTourConfirmationType);
      }
      return setWizardStep(TourWizardSteps.TIME_ZONE);
    } else if (wizardStep === TourWizardSteps.TIME_ZONE && wizardTourConfirmationType && wizardTimeZone) {
      return enableTouringForListing(
        wizardTourConfirmationType,
        wizardTimeZone,
        wizardTourDuration,
        ActionMetadataEntryPoint.WIZARD,
      ).then(() => {
        if (clickStreamMetadata) {
          emitTourAvailabilityWizardTimeZoneNextClickEvent(clickStreamMetadata);
        }
        return setWizardStep(TourWizardSteps.CALENDAR);
      });
    } else if (wizardStep === TourWizardSteps.CALENDAR) {
      if (clickStreamMetadata) {
        try {
          emitCalendarEventActionEvent(clickStreamMetadata, CALENDAR_CLICKSTREAM_ACTION.ADD_TO_LISTING_CTA);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error tracking wizard calendar cta', error);
        }
      }

      if (isInMobileWebView) {
        return mobileHandoffHandler(MOBILE_HANDOFF_TYPES.TOUR_ADDED_SUCCEEDED);
      }

      return onClose();
    }
  };

  const isNextStepDisabled = () => {
    if (wizardStep === TourWizardSteps.CONFIG) {
      return !wizardTourConfirmationType;
    } else if (wizardStep === TourWizardSteps.TIME_ZONE) {
      return (
        !isEnableTouringForListingLoading && (!wizardTourConfirmationType || !wizardTimeZone || !wizardTourDuration)
      );
    }

    return false;
  };

  return (
    <>
      <Modal.Root
        open={true}
        onOpenChange={(open) => open === false && onClose()}
        size="fullScreen"
        // Escape is the only remaining way out when the header exit button is not rendered.
        useDismissProps={{ escapeKey: !hasExitButton, outsidePress: false }}
      >
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content aria-labelledby={TITLE_ID}>
              {isInMobileWebView ? (
                <Modal.Header css={wizardHiddenHeaderStyles}>
                  <span id={TITLE_ID}>{modalHeaderText}</span>
                </Modal.Header>
              ) : (
                <>
                  <Modal.Header css={wizardHeaderStyles}>
                    <Heading
                      id={TITLE_ID}
                      level={modalHeaderLevel ?? 5}
                      css={wizardTitleStyles}
                      data-testid="TourAvailabilityWizard-ModalHeader"
                    >
                      {modalHeaderText}
                    </Heading>
                    {hasExitButton && (
                      <Button
                        tone="neutral"
                        emphasis="outlined"
                        css={wizardExitButtonStyles}
                        onClick={onClose}
                        data-testid="TourAvailabilityWizard-ExitBtn"
                      >
                        Exit
                      </Button>
                    )}
                  </Modal.Header>
                  <Divider />
                </>
              )}
              <Modal.Body css={wizardBodyStyles}>
                <Box css={wizardContainerStyles}>{renderBody()}</Box>
              </Modal.Body>
              <Divider />
              <Modal.Footer css={wizardFooterStyles}>
                <TourAvailabilityCalendarFooter
                  isNextDisabled={isNextStepDisabled()}
                  isNextLoading={isGetTourConfigAndAvailabilityLoading || isEnableTouringForListingLoading}
                  onClickBack={onClickBack}
                  onClickNext={onClickNext}
                  step={wizardStep}
                  hasEnabledTouring={hasEnabledTouring}
                />
              </Modal.Footer>
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
      {timeZoneFromApi && tourConfirmationTypeFromApi && (
        <Modal.Root open={displaySettings} size="fullScreen" scrollDocument>
          <Modal.Portal>
            <Modal.Backdrop>
              <Modal.Content>
                {/* Modal.Header must be rendered (not omitted) to avoid reserved header space;
                   hiding it with display:none suppresses both the header area and close button. */}
                <Modal.Header css={{ display: 'none' }}>
                  <span />
                </Modal.Header>
                <CalendarSettings
                  timeZone={timeZoneFromApi}
                  tourConfirmationType={tourConfirmationTypeFromApi}
                  tourDuration={tourDurationFromApi}
                  canEditTourDuration={canAccessPostingPathTourDuration}
                  onSelectTourConfirmationType={handleSettingsSelectTourConfirmationType}
                  onClickBack={() => setDisplaySettings(false)}
                  isTouringSchedulingEnabled={isTouringSchedulingEnabled}
                  onTourSchedulingToggle={handleTourSchedulingToggle}
                  isDisableTouringForListingLoading={isDisableTouringForListingLoading}
                  calendarStartTime={calendarStartTime}
                  calendarEndTime={calendarEndTime}
                  onChangeCalendarConfigTime={handleChangeCalendarConfigTime}
                  onChangeTourDuration={handleChangeTourDuration}
                />
              </Modal.Content>
            </Modal.Backdrop>
          </Modal.Portal>
        </Modal.Root>
      )}
    </>
  );
};

export default TourAvailabilityWizard;
